import { FiChevronDown } from "react-icons/fi"
import { useFilingSidebarPanel } from "./FilingSidebarAccordion"

type ServiceType = "trademark" | "design" | "patent-direct" | "patent-pct"

const services: Record<ServiceType, { title: string; sections: { title: string; items: string[] }[] }> = {
  trademark: {
    title: "Our services",
    sections: [
      {
        title: "Basic fee",
        items: [
          "Basic fee for representation of an EU trademark application.",
          "Drafting, compilation, and filing of the application and necessary filing documents with the European Union Intellectual Property Office (EUIPO) for one class, excluding preparation of the list of goods and services.",
          "Determination and monitoring of deadlines for the EU trademark application.",
          "Payment of official fees after receipt of payment.",
          "Forwarding of official communications without commentary.",
          "One-time reminder regarding the invoice payment deadline, a prerequisite for payment of official fees.",
          "No monitoring of maintenance or renewal deadlines."
        ]
      },
      {
        title: "Optional services and additional classes",
        items: [
          "Claiming priority, including procurement and submission of a priority document.",
          "Additional fee for a second class, excluding preparation of the list of goods and services.",
          "Additional fee for each further class, excluding preparation of the list of goods and services."
        ]
      }
    ]
  },
  design: {
    title: "Our services",
    sections: [
      {
        title: "Basic fee",
        items: [
          "Base fee for representation regarding the filing of an EU design application for one design.",
          "Drafting, compilation, and filing of the application and necessary filing documents with the European Union Intellectual Property Office (EUIPO), excluding preparation of the list of goods and services.",
          "Determination and monitoring of deadlines for the EU design application.",
          "Payment of official fees after receipt of payment.",
          "Forwarding of official communications without commentary.",
          "One-time reminder regarding the invoice payment deadline, a prerequisite for payment of official fees.",
          "No monitoring of maintenance or renewal deadlines."
        ]
      },
      {
        title: "Optional services and additional designs",
        items: [
          "Claiming priority, including procurement and submission of a priority document.",
          "Additional fee for each additional design in the same application, for designs 2–10.",
          "Additional fee for each additional design in the same application over ten."
        ]
      }
    ]
  },
  "patent-direct": {
    title: "Our services",
    sections: [
      {
        title: "EP patent directly from foreign filing",
        items: [
          "Base fee for representation regarding the filing of an EP patent application.",
          "Drafting, compilation, and filing of the application and necessary filing documents with the European Patent Office (EPO).",
          "Determination and monitoring of deadlines for the EP patent application.",
          "Payment of official fees after receipt of payment.",
          "Forwarding of official communications without commentary.",
          "One-time reminder regarding the invoice payment deadline, a prerequisite for payment of official fees.",
          "No monitoring of maintenance or renewal deadlines.",
          "Claiming priority, including procurement and submission of a priority document."
        ]
      }
    ]
  },
  "patent-pct": {
    title: "Our services",
    sections: [
      {
        title: "EP patent application entering the regional phase of a PCT application",
        items: [
          "Base fee for representation regarding the filing of an EP patent application by entering the regional phase of a PCT application.",
          "Drafting, compilation, and filing of the application and necessary filing documents with the European Patent Office (EPO).",
          "Submission of the request for examination or search request.",
          "Determination and monitoring of deadlines for the EP patent application.",
          "Payment of official fees after receipt of payment.",
          "Forwarding of official communications without commentary.",
          "One-time reminder regarding the invoice payment deadline, a prerequisite for payment of official fees.",
          "No monitoring of maintenance or renewal deadlines.",
          "Claiming priority, including procurement and submission of a priority document."
        ]
      }
    ]
  }
}

export default function ServiceDescriptions({
  type
}: {
  type: ServiceType
}) {
  const [open, toggleOpen] = useFilingSidebarPanel("services")
  const service = services[type]
  const sections = (
    <div className="space-y-4 px-4 py-4 text-xs leading-relaxed text-gray-700">
      {service.sections.map((section) => (
        <section key={section.title}>
          <h3 className="mb-2 font-semibold text-gray-900">{section.title}</h3>
          <ul className="list-disc space-y-1.5 pl-4">
            {section.items.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </section>
      ))}
    </div>
  )

  return (
    <div className="mt-3 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-white text-sm shadow-sm">
      <button
        type="button"
        className="w-full cursor-pointer bg-gray-50 px-4 py-3 text-left font-medium text-dantzBlue"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <span className="flex items-center justify-between gap-3">
          {service.title}
          <FiChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </span>
      </button>
      {open && sections}
    </div>
  )
}
